import { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';

const emptyForm = {
  title: '',
  amount: '',
  currency: 'RUB',
  expected_close_at: '',
  pipeline_id: '',
  stage_id: '',
  crm_company_id: '',
  primary_contact_id: '',
  contact_ids: [],
  is_retainer: false,
  retainer_period_type: 'month',
  retainer_period_days: 30,
  retainer_period_count: '',
  retainer_unlimited: true
};

const emptyCompany = {
  name: '',
  phone: '',
  email: '',
  website: '',
  inn: '',
  address: ''
};

const emptyContact = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  position: '',
  crm_company_id: ''
};

function contactName(contact) {
  return [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Без имени';
}

function DealModal({
  open,
  onClose,
  onSave,
  deal,
  pipeline,
  stages,
  companies = [],
  contacts = [],
  onCreateCompany,
  onCreateContact,
  onDelete,
  onCreateTask,
  onOpenTasks
}) {
  const [form, setForm] = useState(emptyForm);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [contactForm, setContactForm] = useState(emptyContact);

  const pipelineStages = useMemo(
    () => stages.filter((stage) => !form.pipeline_id || stage.pipeline_id === form.pipeline_id),
    [stages, form.pipeline_id]
  );

  const visibleContacts = useMemo(
    () => contacts.filter((contact) => !form.crm_company_id || contact.crm_company_id === form.crm_company_id),
    [contacts, form.crm_company_id]
  );

  useEffect(() => {
    if (!open) return;
    if (deal) {
      setForm({
        title: deal.title || '',
        amount: deal.amount != null ? String(deal.amount) : '',
        currency: deal.currency || 'RUB',
        expected_close_at: deal.expected_close_at ? deal.expected_close_at.slice(0, 10) : '',
        pipeline_id: deal.pipeline_id || pipeline?.id || '',
        stage_id: deal.stage_id || '',
        crm_company_id: deal.crm_company_id || '',
        primary_contact_id: deal.primary_contact_id || '',
        contact_ids: deal.contact_ids || [],
        is_retainer: Boolean(deal.is_retainer),
        retainer_period_type: deal.retainer_period_type || 'month',
        retainer_period_days: deal.retainer_period_days || 30,
        retainer_period_count: deal.retainer_period_count || '',
        retainer_unlimited: deal.retainer_period_count == null
      });
    } else {
      const pipelineId = pipeline?.id || '';
      const firstStage = stages.find((stage) => stage.pipeline_id === pipelineId);
      setForm({
        ...emptyForm,
        pipeline_id: pipelineId,
        stage_id: firstStage ? firstStage.id : ''
      });
    }
  }, [open, deal, pipeline, stages]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.stage_id) return;
    onSave({
      title: form.title.trim(),
      amount: form.amount ? Number(form.amount) : null,
      currency: form.currency || 'RUB',
      expected_close_at: form.expected_close_at ? `${form.expected_close_at}T00:00:00` : null,
      pipeline_id: form.pipeline_id,
      stage_id: form.stage_id,
      crm_company_id: form.crm_company_id || null,
      primary_contact_id: form.primary_contact_id || null,
      contact_ids: form.contact_ids,
      is_retainer: form.is_retainer,
      retainer_period_type: form.is_retainer ? form.retainer_period_type : null,
      retainer_period_days: form.is_retainer && form.retainer_period_type === 'custom_days' ? Number(form.retainer_period_days || 1) : null,
      retainer_period_count: form.is_retainer && !form.retainer_unlimited ? Number(form.retainer_period_count || 1) : null
    });
  };

  const toggleContact = (contactId) => {
    setForm((prev) => {
      const set = new Set(prev.contact_ids);
      if (set.has(contactId)) {
        set.delete(contactId);
      } else {
        set.add(contactId);
      }
      const contactIds = Array.from(set);
      return {
        ...prev,
        contact_ids: contactIds,
        primary_contact_id: prev.primary_contact_id || contactIds[0] || ''
      };
    });
  };

  const handleCreateCompany = async (event) => {
    event.preventDefault();
    if (!companyForm.name.trim()) return;
    const created = await onCreateCompany(companyForm);
    if (!created) return;
    setForm((prev) => ({ ...prev, crm_company_id: created.id }));
    setContactForm((prev) => ({ ...prev, crm_company_id: created.id }));
    setCompanyForm(emptyCompany);
    setCompanyModalOpen(false);
  };

  const handleCreateContact = async (event) => {
    event.preventDefault();
    const created = await onCreateContact({
      ...contactForm,
      crm_company_id: contactForm.crm_company_id || form.crm_company_id || null
    });
    if (!created) return;
    setForm((prev) => ({
      ...prev,
      primary_contact_id: prev.primary_contact_id || created.id,
      contact_ids: Array.from(new Set([...prev.contact_ids, created.id]))
    }));
    setContactForm(emptyContact);
    setContactModalOpen(false);
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title={deal ? 'Редактировать сделку' : 'Новая сделка'} wide>
        <form className="panel__form" onSubmit={handleSubmit}>
          <label>
            Название сделки
            <input type="text" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} required />
          </label>

          <div className="panel__grid">
            <label>
              Сумма
              <input type="number" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
            </label>
            <label>
              Валюта
              <select value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value })}>
                <option value="RUB">RUB</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
            <label>
              План закрытия
              <input type="date" value={form.expected_close_at} onChange={(event) => setForm({ ...form, expected_close_at: event.target.value })} />
            </label>
          </div>

          <div className="panel__grid">
            <label>
              Воронка
              <select
                value={form.pipeline_id}
                onChange={(event) => {
                  const nextPipeline = event.target.value;
                  const firstStage = stages.find((stage) => stage.pipeline_id === nextPipeline);
                  setForm({ ...form, pipeline_id: nextPipeline, stage_id: firstStage ? firstStage.id : '' });
                }}
              >
                {pipeline ? <option value={pipeline.id}>{pipeline.name}</option> : null}
              </select>
            </label>
            <label>
              Стадия
              <select value={form.stage_id} onChange={(event) => setForm({ ...form, stage_id: event.target.value })}>
                {pipelineStages.map((stage) => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="panel__grid">
            <label>
              Компания клиента
              <select
                value={form.crm_company_id || ''}
                onChange={(event) => setForm({ ...form, crm_company_id: event.target.value, primary_contact_id: '', contact_ids: [] })}
              >
                <option value="">-</option>
                {companies.map((companyItem) => (
                  <option key={companyItem.id} value={companyItem.id}>{companyItem.name}</option>
                ))}
              </select>
            </label>
            <div className="crm__inline-actions">
              <button type="button" className="ghost" onClick={() => setCompanyModalOpen(true)}>
                Создать компанию
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setContactForm({ ...emptyContact, crm_company_id: form.crm_company_id || '' });
                  setContactModalOpen(true);
                }}
              >
                Создать контакт
              </button>
            </div>
          </div>

          <div className="panel__grid">
            <label>
              Основной контакт
              <select value={form.primary_contact_id || ''} onChange={(event) => setForm({ ...form, primary_contact_id: event.target.value })}>
                <option value="">-</option>
                {visibleContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>{contactName(contact)}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="panel__field">
            <div className="panel__label">Контакты сделки</div>
            <div className="panel__list">
              {visibleContacts.map((contact) => (
                <label key={contact.id} className="panel__checkbox">
                  <input type="checkbox" checked={form.contact_ids.includes(contact.id)} onChange={() => toggleContact(contact.id)} />
                  {contactName(contact)}
                </label>
              ))}
              {!visibleContacts.length && <div className="muted">Контактов нет.</div>}
            </div>
          </div>

          <label className="panel__checkbox">
            <input type="checkbox" checked={form.is_retainer} onChange={(event) => setForm({ ...form, is_retainer: event.target.checked })} />
            Абонентское обслуживание
          </label>

          {form.is_retainer && (
            <div className="panel__grid">
              <label>
                Отчетный период
                <select value={form.retainer_period_type} onChange={(event) => setForm({ ...form, retainer_period_type: event.target.value })}>
                  <option value="month">Месяц</option>
                  <option value="custom_days">Свое число дней</option>
                </select>
              </label>
              {form.retainer_period_type === 'custom_days' && (
                <label>
                  Дней
                  <input type="number" min="1" value={form.retainer_period_days} onChange={(event) => setForm({ ...form, retainer_period_days: event.target.value })} />
                </label>
              )}
              <label className="panel__checkbox">
                <input type="checkbox" checked={form.retainer_unlimited} onChange={(event) => setForm({ ...form, retainer_unlimited: event.target.checked })} />
                Бесконечно
              </label>
              {!form.retainer_unlimited && (
                <label>
                  Количество периодов
                  <input type="number" min="1" value={form.retainer_period_count} onChange={(event) => setForm({ ...form, retainer_period_count: event.target.value })} />
                </label>
              )}
            </div>
          )}

          <div className="panel__actions">
            {deal && (
              <button type="button" className="ghost danger" onClick={() => onDelete?.(deal)}>
                Удалить
              </button>
            )}
            {deal && onCreateTask && (
              <button type="button" className="ghost" onClick={() => { onClose?.(); onCreateTask(deal); }}>
                Создать задачу
              </button>
            )}
            {deal && onOpenTasks && (
              <button type="button" className="ghost" onClick={() => { onClose?.(); onOpenTasks(deal); }}>
                Задачи сделки
              </button>
            )}
            <button type="button" className="ghost" onClick={onClose}>Отмена</button>
            <button type="submit">Сохранить</button>
          </div>
        </form>
      </Modal>

      <Modal open={companyModalOpen} onClose={() => setCompanyModalOpen(false)} title="Новая компания клиента">
        <form className="panel__form" onSubmit={handleCreateCompany}>
          <label>Название<input type="text" value={companyForm.name} onChange={(event) => setCompanyForm({ ...companyForm, name: event.target.value })} required /></label>
          <div className="panel__grid">
            <label>Телефон<input type="text" value={companyForm.phone} onChange={(event) => setCompanyForm({ ...companyForm, phone: event.target.value })} /></label>
            <label>Email<input type="email" value={companyForm.email} onChange={(event) => setCompanyForm({ ...companyForm, email: event.target.value })} /></label>
          </div>
          <div className="panel__actions">
            <button type="button" className="ghost" onClick={() => setCompanyModalOpen(false)}>Отмена</button>
            <button type="submit">Создать</button>
          </div>
        </form>
      </Modal>

      <Modal open={contactModalOpen} onClose={() => setContactModalOpen(false)} title="Новый контакт">
        <form className="panel__form" onSubmit={handleCreateContact}>
          <div className="panel__grid">
            <label>Имя<input type="text" value={contactForm.first_name} onChange={(event) => setContactForm({ ...contactForm, first_name: event.target.value })} /></label>
            <label>Фамилия<input type="text" value={contactForm.last_name} onChange={(event) => setContactForm({ ...contactForm, last_name: event.target.value })} /></label>
          </div>
          <div className="panel__grid">
            <label>Телефон<input type="text" value={contactForm.phone} onChange={(event) => setContactForm({ ...contactForm, phone: event.target.value })} /></label>
            <label>Email<input type="email" value={contactForm.email} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} /></label>
          </div>
          <label>
            Компания
            <select value={contactForm.crm_company_id || ''} onChange={(event) => setContactForm({ ...contactForm, crm_company_id: event.target.value })}>
              <option value="">-</option>
              {companies.map((companyItem) => (
                <option key={companyItem.id} value={companyItem.id}>{companyItem.name}</option>
              ))}
            </select>
          </label>
          <div className="panel__actions">
            <button type="button" className="ghost" onClick={() => setContactModalOpen(false)}>Отмена</button>
            <button type="submit">Создать</button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default DealModal;
